import { Component, computed, DestroyRef, effect, inject, Injector, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
	OrganizationInsurer,
	OrganizationInsurerPostPayload,
} from '@/organization/organization/organization-insurer/organization-insurer.model';
import { OrganizationInsurerApiService } from '@/organization/organization/organization-insurer/organization-insurer.api.service';
import { finalize, forkJoin, map, Observable } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { Insurer } from '@/codebook/insurer/_types/insurer.type';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatInput } from '@angular/material/input';
import { OrganizationService } from '@/organization/organization/organization.service';
import { CustomMatLoaderComponent } from '@/common/custom-mat-loader/custom-mat-loader.component';

type FormControls<T> = {
	[K in keyof T]: FormControl<T[K]>;
};

@Component({
	selector: 'app-organization-insurer',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatExpansionModule,
		MatButtonModule,
		CKEditorModule,
		MatSelectModule,
		MatInput,
		CustomMatLoaderComponent,
	],
	templateUrl: './organization-insurer.component.html',
	styles: `
		::ng-deep .insurer-editor .ck-editor__editable {
			max-height: 400px !important;
		}
	`,
})
export class OrganizationInsurerComponent implements OnInit {
	readonly editor = ClassicEditor;
	readonly editorConfig = {
		toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'],
	};

	readonly isActive = input(false);
	readonly organizationId = input.required<number>();
	readonly travelOrganizationGovernmentApproval = input.required<string>();

	readonly form = signal<FormGroup>(null);
	readonly insurersList = signal<Insurer[]>([]);
	readonly alreadyAddedInsurersIds = signal<number[]>([]);
	readonly isLoading = signal(false);

	readonly insurersFormArray = computed(
		() => this.form().get('insurers') as FormArray<FormGroup<FormControls<OrganizationInsurer>>>,
	);
	readonly filteredInsurersList = computed(() => {
		return this.insurersList().filter((insurer) => !this.alreadyAddedInsurersIds().includes(insurer.id));
	});

	readonly #injector = inject(Injector);

	private readonly fb = inject(FormBuilder);
	private readonly organizationInsurerApiService = inject(OrganizationInsurerApiService);
	private readonly infobox = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly organizationService = inject(OrganizationService);

	constructor() {
		this.form.set(
			this.fb.group({
				insurerToAdd: [null, [Validators.required]],
				travelOrganizationGovernmentApproval: '',
				insurers: this.fb.array([]),
			}),
		);
	}

	ngOnInit(): void {
		this.isLoading.set(true);
		this.initDataLoading();
		this.subscribeToInsurerIds();
		this.form()
			.get('travelOrganizationGovernmentApproval')
			.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => this.organizationService.travelOrganizationGovernmentApproval.set(value));
	}

	addInsurer(): void {
		const insurerToAddControl = this.form().get('insurerToAdd');
		insurerToAddControl.markAllAsTouched();

		if (insurerToAddControl.invalid) {
			return;
		}

		const newInsurerGroup = this.createInsurerFormGroup();

		this.insurersFormArray().push(newInsurerGroup as FormGroup);
	}

	save(insurer: FormGroup<FormControls<OrganizationInsurer>>): void {
		insurer.markAllAsTouched();
		if (insurer.invalid) {
			this.infobox.warning('Uzupełnij wszystkie opisy zanim zapiszesz ubezpieczyciela');
			return;
		}

		this.isLoading.set(true);

		const payload: OrganizationInsurerPostPayload = {
			...(insurer.value as OrganizationInsurer),
			insurer: insurer.value.insurer.id,
		};

		const actionName = payload.id ? 'updateOrganizationInsurer' : 'createOrganizationInsurer';

		this.organizationInsurerApiService[actionName](this.organizationId(), payload)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((value) => {
				insurer.get('id')?.setValue(value?.id);
				this.infobox.success('Pomyślnie zapisano!');
			});
	}

	updateTravelOrganizationGovernmentApproval(): void {
		const travelOrganizationGovernmentApproval = this.form().get('travelOrganizationGovernmentApproval').value;
		this.organizationInsurerApiService
			.patchOrganization(this.organizationId(), {
				travelOrganizationGovernmentApproval,
			})
			.subscribe(() => this.infobox.success('Pomyślnie zaktualizowano zezwolenie marszałka'));
	}

	private initDataLoading(): void {
		const loadDataEffect = effect(
			() => {
				if (this.isActive() && !this.insurersFormArray().value.length) {
					this.loadCombinedInsurerData();
					this.form()
						.get('travelOrganizationGovernmentApproval')
						.patchValue(this.travelOrganizationGovernmentApproval());
					loadDataEffect.destroy();
				}
			},
			{ injector: this.#injector },
		);
	}

	private loadCombinedInsurerData(): void {
		forkJoin({
			insurerList: this.loadInsurerList(),
			organizationInsurers: this.loadOrganizationInsurers(),
		})
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe(({ insurerList, organizationInsurers }) => {
				this.insurersList.set(insurerList);
				const formArray = this.insurersFormArray();

				organizationInsurers.forEach((item) => {
					const insurerFormGroup = this.createInsurerFormGroup(item);
					formArray.push(insurerFormGroup as FormGroup);
				});
			});
	}

	private loadInsurerList(): Observable<Insurer[]> {
		return this.organizationInsurerApiService.getInsurerList();
	}

	private loadOrganizationInsurers(): Observable<OrganizationInsurer[]> {
		return this.organizationInsurerApiService.getOrganizationInsurerList(this.organizationId());
	}

	private subscribeToInsurerIds(): void {
		this.insurersFormArray()
			.valueChanges.pipe(
				map((values) => values.map((val) => val.insurer.id)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((ids) => this.alreadyAddedInsurersIds.set(ids));
	}

	private createInsurerFormGroup(
		data?: OrganizationInsurer,
	): FormGroup<FormControls<Omit<OrganizationInsurer, 'insuredListConst'>> & { insuredListConst: FormGroup }> {
		const insurer = data?.insurer ?? this.form().get('insurerToAdd')?.value;
		const insuredListHeaders = insurer?.insuredListHeaders ?? [];
		const insuredListConstGroup = this.createInsuredListConstGroup(data?.insuredListConst, insuredListHeaders);

		return this.fb.group({
			id: this.fb.control(data?.id ?? null),
			organization: this.fb.control(data?.organization ?? this.organizationId()),
			insurer: this.fb.control(insurer),
			inComments: this.fb.control(data?.inComments ?? '', Validators.required),
			reservationSummary: this.fb.control(data?.reservationSummary ?? '', Validators.required),
			beforeInsuranceSection: this.fb.control(data?.beforeInsuranceSection ?? '', Validators.required),
			insuranceSection: this.fb.control(data?.insuranceSection ?? '', Validators.required),
			afterInsuranceSection: this.fb.control(data?.afterInsuranceSection ?? '', Validators.required),
			insuredListConst: insuredListConstGroup,
		});
	}

	private createInsuredListConstGroup(insuredListConst?: Record<string, string>, headers: string[] = []): FormGroup {
		const controls = headers.reduce(
			(acc, header) => {
				const value = insuredListConst?.[header] ?? '';
				acc[header] = this.fb.control(value, Validators.required);
				return acc;
			},
			{} as Record<string, FormControl<string>>,
		);

		return this.fb.group(controls);
	}
}
